import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";

export const examAdminQuestionDetail = {
  pageTitle: "Question Details",
  notFound: "The requested question was not found.",
  subTitleVersioned: "Question version code {versionedCode}",
  actions: {
    publish: "Publish Question",
    archive: "Archive",
    viewing: "Viewing",
    viewVersion: "View this version",
  },
  tabs: {
    preview: "Candidate View",
    config: "Question Type Config",
    experience: "Full Info",
    versions: "Version History",
    json: "Structured JSON",
  },
  archiveConfirm: {
    title: "Archive this question?",
    description: "Archived questions are hidden from the default list.",
  },
  archivedAlert: {
    message: "This question is archived",
    description:
      "Archived questions are displayed as read-only by default. To continue iterating, copy the structure into a new question or restore it to draft before editing.",
  },
  sections: {
    basicInfo: "Basic Info",
    versions: "Version History and Diffs",
    content: "Question Content and Structured Data",
  },
  versionDiff: {
    expandLabel: "Expand version history and diffs",
    compareA: "Compare Version A",
    compareB: "Compare Version B",
    compareTitle: "Compare: {from} -> {to}",
    scalarChanges: "Scalar Changes ({count})",
    jsonChanges: "JSON Changes ({count})",
    rawJsonChanges: "Raw JSON Changes ({count})",
    pickDifferent: "Please select two different versions to compare.",
    viewingCurrent: "Currently viewing:",
    singleVersionLabel: "Current version:",
    singleVersionHint:
      "There is only one version right now, so no version comparison is available.",
    backToLatest: "Back to latest version",
    snapshotTitle: "Version snapshot: {code}",
    loadSnapshotFailed: "Failed to load version snapshot",
    summary: {
      total: "Total changes {count}",
      scalar: "Scalar {count}",
      json: "JSON {count}",
      cases: "Case changes {count}",
    },
    readable: {
      title: "Readable Changes",
      stem: "Stem",
      starterCode: "Starter Code",
      publicCases: "Public Cases",
      hiddenCases: "Hidden Cases",
      judgeConfig: "Judge Config",
      analysis: "Analysis",
      metadata: "AI Metadata",
      choiceSection: "Choice/Judgment Diffs",
      shortAnswerSection: "Short Answer Diffs",
      environmentSection: "Environment Config Diffs",
      starterCodeCompare: "Starter Code Compare",
      caseChanges: "Case Diff Details",
      caseScope: "Case Group",
      caseNo: "Case No.",
      caseField: "Field",
    },
  },
  overview: {
    type: "Question Type",
    status: commonShortNouns.status,
    currentVersion: "Current Version",
    difficulty: "Difficulty",
    reviewStatus: "Review Status",
    mainCode: "Question Code",
    versionedCode: "Question Version Code",
    createdAt: commonShortFieldLabels.createdAt,
    latestVersionCreatedAt: "Latest Version Created At",
    internalNote: "Note (Admin Only)",
  },
  content: {
    candidateView: "Candidate View",
    typeConfig: "Question Type Config",
    structuredJson: "Structured JSON",
    previewTitle: "Question Preview (Candidate View)",
    codeConfigTitle: "Programming Question Config",
    tryRunTitle: "Online Try Run (Admin)",
    practicalConfigTitle: "Practical Question Config (Form View)",
    attackDefenseConfigTitle: "Attack & Defense Container Config (Form View)",
    adminExperienceTitle: "Admin Experience (Current Session)",
    rawJsonContent: "content_json (Raw)",
    rawJsonAnswer: "answer_json (Raw)",
    rawJsonAnalysis: "analysis_json (Raw)",
    rawJsonAi: "ai_metadata_json (Raw)",
  },
  paperReferences: {
    title: "Paper References",
    empty: "This question is not referenced by any paper yet",
    total: "Total References",
    paperCount: "Papers",
    publishedPaperCount: "Published Papers",
    currentVersionRefs: "Current Paper Version References",
    latestQuestionVersionRefs: "Latest Question Version References",
    latestPaper: "Most Recent Referencing Paper",
    viewAll: "View all reference details",
    drawerTitle: "Paper Reference Details ({count})",
    table: {
      paper: "Paper",
      status: commonShortNouns.status,
      paperVersion: "Paper Version",
      questionVersion: "Question Version",
      position: "Position",
      score: "Score",
      actions: "Actions",
    },
    labels: {
      current: "Current",
      history: "Historical",
      latest: "Latest",
      outdated: "Outdated",
    },
  },
  question: {
    taskTitle: "Question Name",
    taskDescription: "Question Description",
    stem: "Stem",
    options: "Options",
    optionFallback: "Option {index}",
    correctAnswer: "Correct Answer (Admin Only)",
    analysis: "Analysis",
    referenceAnswer: "Reference Answer (Admin Only)",
    keywords: "Keywords (Admin Only)",
    keyPoints: "Scoring Points (Admin Only)",
  },
  environment: {
    environmentType: "Environment Type",
    environmentService: "Environment Service",
    resourceProvider: "Resource Provider",
    checkMode: "Check Mode",
    labService: "Lab Service",
    image: "Image Identifier",
    containerPort: "Container Port",
    accessHint: "Access Hint",
    dynamicFlag: "Dynamic Flag",
    dynamicFlagOn: "Enabled",
    dynamicFlagOff: "Disabled",
    deliveryMode: "Carrier",
    staticCarrier: "Static Question Carrier",
    carrierAttachment: "Attachment",
    carrierContainer: "Container",
    attachmentRefs: "Attachment References",
    standardFlag: "Standard Flag (Admin Only)",
    hint: "Hint",
  },
  code: {
    supportedLanguages: "Supported Languages",
    defaultLanguage: "Default Language",
    timeLimit: "Time Limit",
    memoryLimit: "Memory Limit",
    executionHint: "Execution Hint",
    starterCode: "Starter Code",
    starterCodeEmpty: "// No starter code",
    publicCases: "Example/Public Cases",
    caseTitle: "Case {index}",
    publicCaseLabel: "Public Case {index}",
    input: "Input",
    output: "Output",
    explanation: "Explanation",
    hiddenCaseCount: "Hidden Case Count",
    hiddenCasesTitle: "Hidden Cases (Admin Only)",
    hiddenCaseTitle: "Hidden Case {index}",
    hiddenCaseLabel: "Hidden Case {index}",
  },
  tryRun: {
    runLanguage: "Run Language",
    sourceCode: "Code",
    tip: "Supports indentation and syntax highlighting. Shortcut: Ctrl/Cmd + Enter to run.",
    runAllCases: "Run All Cases",
    running: "Running all test cases",
    runningDescription: "Please wait. The dialog will update automatically after completion.",
    failed: "Run failed",
    summary: "{passed}/{total} cases passed",
    actualOutput: "Actual output",
    saveAsNewVersion: "Save as New Version (+1)",
    fillStarterCode: "Fill Starter Code",
    result: "Result:",
    rawDetail: "Judge Details (Raw)",
    selectLanguage: "Please select the language",
    inputCode: "Please input code",
    verdictContractAnomaly: "Run verdict contract anomaly: {value}",
    caseStatusContractAnomaly: "Case status contract anomaly: {value}",
  },
  practical: {
    containerInstance: "Container Instance",
    instanceStatus: "Instance Status",
    webEndpoint: "Web Endpoint",
    notCreated: "Not Created",
    rerunInitScript: "Rerun Init Script",
    runCheckScript: "Run Check Script",
    initScript: "Init Script",
    checkScript: "Check Script",
    initScriptEditable: "Init Script (Editable Before Rerun)",
    checkScriptEditable: "Check Script (Editable Before Run)",
    lastAction: "Last action:",
    lastActionInit: "Init Script",
    lastActionCheck: "Check Script",
    attackDefenseCheckScriptNote:
      "Attack & defense experiences do not use a check script. Enter the Flag manually for validation instead.",
    flagValidation: "Flag Validation (Admin Experience)",
    flagPlaceholder: "Enter flag{...}",
    validateFlag: "Validate Flag",
    lastValidation: "Last validation result:",
    validationCorrect: "Correct",
    validationIncorrect: "Incorrect",
    debugNote:
      "You can modify the scripts here and then create or rerun the environment for validation. The current edits are for debugging only and are not saved back to the question configuration automatically.",
    debugInitOnlyNote:
      "You can modify the init script here and then create or rerun the environment for validation. The current edits are for debugging only and are not saved back to the question configuration automatically.",
    initScriptDebug: "Init Script (Debug Edit)",
    checkScriptDebug: "Check Script (Debug Edit)",
    callbackModeNoCheck:
      "The current mode is callback, so a platform-side check script is usually unnecessary.",
  },
  messages: {
    updated: "Question updated",
    updateSuccess: "Question updated successfully. A new version is created only when content changes.",
    deleteSuccess: "Question deleted successfully",
    publishSuccess: "Question published",
    archiveSuccess: "Question archived",
    tryRunSuccess: "Try run completed",
    tryRunSavedAsVersion: "Saved as new version V{versionNo}",
    envNotSupport: "This question does not support container experience",
    imageMissing: "This question has no image identifier configured",
    initScriptFailed: "Init script execution failed",
    sessionPersistFailed: "Experience environment created, but session persistence failed",
    envCreateInitFailed: "Experience environment created, but init script failed: {error}",
    envCreateInitSuccess: "Experience environment created and init script executed: {status}",
    envCreateSuccess: "Experience environment created",
    pleaseCreateEnv: "Please create the experience environment first",
    scriptEmptyInit: "Init script is empty",
    scriptEmptyCheck: "Check script is empty",
    actionInit: "Initialize",
    actionCheck: "Check",
    actionSuccess: "{action} succeeded",
    actionFailed: "{action} failed ({status}): {detail}",
    checkScriptPassed: "Check script passed (exit code 0)",
    checkScriptFailed: "Check script failed (exit code {exitCode})",
    nonZeroExit: "Script returned a non-zero exit code",
    notAttackDefenseEnv: "This is not an attack & defense question with a container experience",
    pleaseInputFlag: "Please input flag first",
    dynamicFlagMissing: "Dynamic flag environment variable was not found in the container: {envKey}",
    staticFlagMissing: "Static flag is not configured",
    flagPass: "Flag validation passed",
    flagWrong: "Flag is incorrect. Please try again.",
    envDestroyed: "Experience environment destroyed",
    sessionCleaned:
      "Invalid experience environment session cleaned. Please create the experience environment again.",
  },
  confirmDelete: {
    title: "Delete this question?",
    description: "Only questions not referenced by papers or attempts can be deleted.",
  },
  edit: {
    title: "Edit Question",
    note: "A new version is created and the version number increments only when question content changes. Status-only changes, such as draft or published, do not change the version.",
    statusLabel: "Status",
    saveChanges: "Save Changes",
    preview: "Preview",
    previewTitle: "Question Preview (Candidate View)",
  },
  table: {
    field: "Field",
    oldValue: "Old Value",
    newValue: "New Value",
    jsonField: "JSON Field",
    path: "Path",
    changeType: "Change Type",
    versionNo: "Version",
    versionedCode: "Question Version Code",
    title: "Title",
    createdAt: commonShortFieldLabels.createdAt,
    actions: "Actions",
  },
  diffChangeType: {
    added: "Added",
    removed: "Removed",
    updated: "Updated",
  },
  rawJsonTitle: "Structured JSON",
} as const;
